import { createContext, useContext, useEffect, useState } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { identifyUser, trackUserLogin, trackUserLogout, resetUser } from "@/lib/analytics";
import { initializeAcademicProfile } from "@/lib/academicLevels";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        
        // Phase 2.3.A: Track authentication events
        if (event === 'SIGNED_IN' && session?.user) {
          const authMethod = session.user.app_metadata.provider || 'email';
          trackUserLogin(session.user.id, authMethod as 'email' | 'google' | 'github');
          identifyUser(session.user.id, {
            email: session.user.email,
            created_at: session.user.created_at,
          });
          
          // Phase 2: Initialize academic profile cache (non-blocking)
          initializeAcademicProfile().catch(console.error);
        } else if (event === 'SIGNED_OUT') {
          resetUser();
        } else if (event === 'USER_UPDATED' && session?.user) {
          identifyUser(session.user.id, {
            email: session.user.email,
          });
        }
      }
    );

    // Get initial session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      
      // Phase 2.3.A: Identify existing user
      if (session?.user) {
        identifyUser(session.user.id, {
          email: session.user.email,
          created_at: session.user.created_at,
        });
        
        // Phase 2: Initialize academic profile cache (non-blocking)
        initializeAcademicProfile().catch(console.error);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    // Phase 2.3.A: Track logout before signing out
    trackUserLogout();
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};